import React, { useMemo } from 'react';
import {
  Heading,
  SimpleGrid,
  Box,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useInView } from 'react-intersection-observer';
import {
  STATS_2025_LABELS,
  STATS_2025_VALUE_ADD,
  ISCRIZIONI_TOTALI_2025,
  ORE_ANIMATORI_2025,
  PCTO_ATTIVATI_2025,
  CIL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
  TESTIMONIALS,
} from './constants';
import { wrapped2025Sections } from '../../data/wrapped2025Sections';
import CountUp from '../CountUp';

const GHIACCIOLI_CE_2025 =
  wrapped2025Sections.find((s) => s.id === 'ghiaccioli')?.value ?? 612;

const STAR = '★';

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const TestimonialCard = ({ t, isCentered }) => (
  <Box
    bg="white"
    borderRadius="xl"
    boxShadow="0 4px 20px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.06)"
    borderWidth="1px"
    borderColor="gray.100"
    p={{ base: 4, md: 5 }}
    textAlign="center"
    maxW={isCentered ? { md: '400px' } : undefined}
    mx={isCentered ? { md: 'auto' } : undefined}
  >
    <Text
      fontSize="lg"
      color={CIL.yellow}
      letterSpacing="2px"
      mb={3}
      aria-hidden
      textShadow="0 1px 2px rgba(0, 0, 0, 0.12)"
    >
      {STAR.repeat(5)}
    </Text>
    <Text
      fontStyle="italic"
      color="gray.800"
      fontSize={{ base: 'md', md: 'lg' }}
      lineHeight="tall"
    >
      &quot;{t.text}&quot;
    </Text>
    <Text fontWeight="600" mt={3} color={CIL.plum} fontSize="sm">
      — {t.author}
    </Text>
  </Box>
);

const LABELS_WITH_PERCENT = [
  STATS_2025_LABELS.soldOut,
  STATS_2025_LABELS.pagamentiElettronici,
  STATS_2025_LABELS.famiglieSoddisfatte,
  STATS_2025_LABELS.ragazziTornare,
];

const StatBlock = ({ value, label, valueAdd, inView, decimals, separator }) => (
  <Box textAlign="center" py={6}>
    <Text
      fontSize={{ base: '3xl', md: '4xl' }}
      fontWeight="bold"
      color={CIL.plum}
      lineHeight="1"
    >
      <CountUp
        from={0}
        to={value}
        startCounting={inView}
        duration={1.2}
        decimals={decimals}
        separator={separator}
      />
      {LABELS_WITH_PERCENT.includes(label) && '%'}
    </Text>
    <Text mt={2} fontSize="sm" fontWeight="bold" color="gray.600">
      {label}
    </Text>
    {valueAdd && (
      <Text mt={1} fontSize="xs" color="gray.500" fontStyle="italic">
        {valueAdd}
      </Text>
    )}
  </Box>
);

const StatisticsSection = () => {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const desktopFive = useMemo(() => shuffle(TESTIMONIALS).slice(0, 5), []);
  const mobileThree = useMemo(() => shuffle(TESTIMONIALS).slice(0, 3), []);
  const toShow = isMobile ? mobileThree : desktopFive;

  return (
    <Box
      ref={ref}
      as="section"
      id="section-recap-2025"
      py={{ base: 10, md: 14 }}
      scrollMarginTop={{ base: '80px', md: '140px' }}
    >
      <Box w="100%" maxW="100%" px={{ base: 4, md: 6, lg: 8 }}>
        <Heading as="h2" size="lg" mb={8} textAlign="center">
          <Box
            w="100%"
            px={4}
            py={3}
            borderRadius="lg"
            sx={{ background: SECTION_TITLE_CONTAINER_BG }}
            boxShadow="sm"
          >
            <Text as="span" fontWeight="bold" color={SECTION_TITLE_TEXT_COLOR}>
              Recap Centro estivo 2025
            </Text>
          </Box>
        </Heading>
        {/* Blocco statistiche */}
        <Box
          bg="white"
          borderRadius="xl"
          boxShadow="sm"
          borderWidth="1px"
          borderColor="gray.100"
          p={{ base: 6, md: 8 }}
          w="100%"
          maxW="100%"
          mb={8}
        >
          <Text
            mb={6}
            textAlign="center"
            fontSize="sm"
            color="gray.500"
            fontStyle="italic"
          >
            I dati provengono dal questionario 2025.
          </Text>
          <SimpleGrid
            columns={{ base: 2, md: 4 }}
            spacing={8}
            w="100%"
            maxW="100%"
          >
            <StatBlock
              value={GHIACCIOLI_CE_2025}
              label={STATS_2025_LABELS.ghiaccioli}
              valueAdd={STATS_2025_VALUE_ADD.ghiaccioli}
              inView={inView}
            />
            <StatBlock
              value={81.6}
              label={STATS_2025_LABELS.pagamentiElettronici}
              valueAdd={STATS_2025_VALUE_ADD.pagamentiElettronici}
              inView={inView}
              decimals={1}
            />
            <StatBlock
              value={ISCRIZIONI_TOTALI_2025}
              label={STATS_2025_LABELS.iscrizioniTotali}
              valueAdd={STATS_2025_VALUE_ADD.iscrizioniTotali}
              inView={inView}
            />
            <StatBlock
              value={ORE_ANIMATORI_2025}
              label={STATS_2025_LABELS.oreAnimatori}
              valueAdd={STATS_2025_VALUE_ADD.oreAnimatori}
              inView={inView}
              separator="."
            />
            <StatBlock
              value={1}
              label={STATS_2025_LABELS.animatoreOgni10}
              valueAdd={STATS_2025_VALUE_ADD.animatoreOgni10}
              inView={inView}
            />
            <StatBlock
              value={100}
              label={STATS_2025_LABELS.famiglieSoddisfatte}
              valueAdd={STATS_2025_VALUE_ADD.famiglieSoddisfatte}
              inView={inView}
            />
            <StatBlock
              value={100}
              label={STATS_2025_LABELS.ragazziTornare}
              valueAdd={STATS_2025_VALUE_ADD.ragazziTornare}
              inView={inView}
            />
            <StatBlock
              value={PCTO_ATTIVATI_2025}
              label={STATS_2025_LABELS.pctoAttivati}
              valueAdd={STATS_2025_VALUE_ADD.pctoAttivati}
              inView={inView}
            />
          </SimpleGrid>
        </Box>

        {/* Recensioni: ognuna in un box con border radius */}
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={6}
          w="100%"
          maxW="100%"
        >
          {toShow.map((t, i) => (
            <Box
              key={t.text}
              gridColumn={!isMobile && i === 4 ? { md: '1 / -1' } : undefined}
            >
              <TestimonialCard t={t} isCentered={!isMobile && i === 4} />
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default StatisticsSection;
